import React, { useRef, useState, type FormEvent } from "react";
import {
  astryx,
  AuthCard,
  AuthLayout,
  AuthLink,
  AuthSubmitButton,
  AuthTextInput,
  PasswordInput,
} from "@shadowcue/ui";
import useNativeFormSubmit from "../../hooks/useNativeFormSubmit";

interface EditAccountProps {
  /** Devise's registration path — the update PUTs here, the delete DELETEs. */
  formAction: string;
  authenticityToken: string;
  email: string;
  openaiKey: string | null;
  /** Devise's configured minimum, shown in the new password's placeholder. */
  minimumPasswordLength: number | null;
  /** Devise's validation messages, each shown on the field it belongs to. */
  emailError: string | null;
  passwordError: string | null;
  passwordConfirmationError: string | null;
  currentPasswordError: string | null;
  dashboardPath: string;
}

/**
 * The account screen (Devise registrations#edit). Posts natively to Devise,
 * which updates through `update_with_password` — so nothing changes without
 * the current password, and leaving the new password blank keeps the old one.
 *
 * Deleting posts a second, hidden form: that keeps the destructive action
 * inside the card without nesting one form in another.
 */
const EditAccount = (props: EditAccountProps) => {
  const [email, setEmail] = useState<string>(props.email);
  const [password, setPassword] = useState<string>("");
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>("");
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [openaiKey, setOpenaiKey] = useState<string>(props.openaiKey ?? "");
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const deleteFormRef = useRef<HTMLFormElement>(null);
  const { isSubmitting, markSubmitting } = useNativeFormSubmit();

  // Once the email is edited, Devise's error was about a different address.
  const isShowingEmailError = props.emailError !== null && email === props.email;

  // Devise never refills password fields, so a field that's still empty is one
  // the user hasn't retyped since the error. Typing clears its error.
  const passwordError = password === "" ? props.passwordError : null;
  const passwordConfirmationError =
    passwordConfirmation === "" ? props.passwordConfirmationError : null;
  const currentPasswordError = currentPassword === "" ? props.currentPasswordError : null;

  const passwordPlaceholder = props.minimumPasswordLength
    ? `New password (${props.minimumPasswordLength}+ characters)`
    : "New password";

  const canSubmit = currentPassword !== "" && !isSubmitting;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (!canSubmit) {
      event.preventDefault();
      return;
    }
    markSubmitting();
  };

  const onDelete = () => {
    setIsDeleting(true);
    deleteFormRef.current?.submit();
  };

  return (
    <AuthLayout>
      <div>
        <form method="post" action={props.formAction} onSubmit={onSubmit}>
          <input type="hidden" name="_method" value="put" />
          <input type="hidden" name="authenticity_token" value={props.authenticityToken} />
          <AuthCard subtitle="Manage your account.">
            <astryx.Stack direction="vertical" gap={3}>
              <AuthTextInput
                type="email"
                label="Email"
                value={email}
                onChange={setEmail}
                isLabelHidden
                placeholder="Email"
                htmlName="user[email]"
                status={
                  isShowingEmailError
                    ? { type: "error", message: props.emailError ?? "" }
                    : undefined
                }
                // Matches the password fields, whose InputGroup always detaches.
                statusVariant="detached"
                isRequired
                width="100%"
              />
              <AuthTextInput
                type="password"
                label="OpenAI API key"
                value={openaiKey}
                onChange={setOpenaiKey}
                isLabelHidden
                placeholder="OpenAI API key"
                htmlName="user[openai_key]"
                width="100%"
              />
            </astryx.Stack>

            <astryx.Stack direction="vertical" gap={3}>
              <astryx.Text type="supporting" color="secondary">
                Leave these blank to keep your current password.
              </astryx.Text>
              <PasswordInput
                label="New password"
                value={password}
                onChange={setPassword}
                placeholder={passwordPlaceholder}
                htmlName="user[password]"
                errorMessage={passwordError}
                isRequired={false}
              />
              <PasswordInput
                label="Confirm new password"
                value={passwordConfirmation}
                onChange={setPasswordConfirmation}
                placeholder="Confirm new password"
                htmlName="user[password_confirmation]"
                errorMessage={passwordConfirmationError}
                isRequired={false}
              />
            </astryx.Stack>

            <astryx.Stack direction="vertical" gap={3}>
              <astryx.Text type="supporting" color="secondary">
                Your current password confirms any change.
              </astryx.Text>
              <PasswordInput
                label="Current password"
                value={currentPassword}
                onChange={setCurrentPassword}
                placeholder="Current password"
                htmlName="user[current_password]"
                errorMessage={currentPasswordError}
              />
            </astryx.Stack>

            <astryx.Stack direction="vertical" gap={3}>
              <AuthSubmitButton
                type="submit"
                label={isSubmitting ? "Saving…" : "Save changes"}
                variant="primary"
                width="100%"
                isDisabled={!canSubmit}
                isLoading={isSubmitting}
              />
              <astryx.Stack direction="horizontal" justify="center">
                <AuthLink href={props.dashboardPath}>Back to dashboard</AuthLink>
              </astryx.Stack>
            </astryx.Stack>

            <astryx.Divider />

            <astryx.Stack direction="vertical" gap={3}>
              <astryx.Text type="supporting" color="secondary">
                Deleting your account is permanent.
              </astryx.Text>
              <astryx.Button
                type="button"
                label="Delete account"
                variant="destructive"
                width="100%"
                isDisabled={isDeleting}
                onClick={() => setIsDeleteOpen(true)}
              />
            </astryx.Stack>
          </AuthCard>
        </form>

        <form hidden ref={deleteFormRef} method="post" action={props.formAction}>
          <input type="hidden" name="_method" value="delete" />
          <input type="hidden" name="authenticity_token" value={props.authenticityToken} />
        </form>

        <astryx.AlertDialog
          isOpen={isDeleteOpen}
          onOpenChange={setIsDeleteOpen}
          title="Delete your account?"
          description="This can't be undone, and you'll be signed out immediately."
          actionLabel="Delete account"
          actionVariant="destructive"
          isActionLoading={isDeleting}
          onAction={onDelete}
        />
      </div>
    </AuthLayout>
  );
};

export default EditAccount;
